import { buildWordTool, type WordTool } from './builder';
import { HIGHLIGHT_COLORS } from './constants';

const REPLACE_FORMAT_KEYS = [
  'bold',
  'italic',
  'underline',
  'underline_style',
  'strikethrough',
  'color',
  'size',
  'font',
  'highlight',
] as const;

function validateReplaceFormat(
  f: Record<string, unknown>,
): { error: string; recovery: string } | null {
  if (typeof f.color === 'string') {
    if (f.color.startsWith('#')) {
      return {
        error: 'replace_format.color should not include the # prefix.',
        recovery: `Use "${f.color.slice(1)}" instead of "${f.color}".`,
      };
    }
    if (!/^[0-9A-Fa-f]{6}$/.test(f.color)) {
      return {
        error: `Invalid replace_format.color "${f.color}".`,
        recovery: 'Use a 6-character hex without #. Example: "FF0000".',
      };
    }
  }
  if (typeof f.size === 'number' && f.size <= 0) {
    return { error: 'replace_format.size must be > 0.', recovery: 'Use 11, 12, etc.' };
  }
  if (
    typeof f.highlight === 'string' &&
    !HIGHLIGHT_COLORS.includes(f.highlight as (typeof HIGHLIGHT_COLORS)[number])
  ) {
    return {
      error: `Invalid replace_format.highlight "${f.highlight}".`,
      recovery: `Use one of: ${HIGHLIGHT_COLORS.join(', ')}.`,
    };
  }
  return null;
}

function applyReplaceFormat(range: Word.Range, f: Record<string, unknown>) {
  if (typeof f.bold === 'boolean') range.font.bold = f.bold;
  if (typeof f.italic === 'boolean') range.font.italic = f.italic;
  if (typeof f.strikethrough === 'boolean') range.font.strikeThrough = f.strikethrough as boolean;
  if (typeof f.underline_style === 'string') {
    const map: Record<string, Word.UnderlineType> = {
      none: Word.UnderlineType.none,
      single: Word.UnderlineType.single,
      double: Word.UnderlineType.double,
      dotted: Word.UnderlineType.dotted,
      dashed: Word.UnderlineType.dashLine,
      wave: Word.UnderlineType.wave,
    };
    range.font.underline = map[f.underline_style] ?? Word.UnderlineType.none;
  } else if (typeof f.underline === 'boolean') {
    range.font.underline = f.underline ? Word.UnderlineType.single : Word.UnderlineType.none;
  }
  if (typeof f.color === 'string') range.font.color = f.color;
  if (typeof f.size === 'number') range.font.size = f.size;
  if (typeof f.font === 'string') range.font.name = f.font;
  if (typeof f.highlight === 'string') range.font.highlightColor = f.highlight;
}

export const searchTools: WordTool[] = [
  buildWordTool({
    name: 'search_document',
    description:
      'Search the document body, with optional find-and-replace and optional inline formatting applied to the replaced text — all in one call. For delete-all-matches, pass replace_with="". For replace-and-format (e.g. replace "TODO" with "DONE" and make it bold), pass replace_format. Searches body text only (not headers/footers/comments/tables). Case-insensitive by default.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description:
            'Text to search for in the body (not headers/footers/comments). Case-insensitive by default. Use 3+ unique words for short queries to avoid over-matching.',
        },
        replace_with: {
          type: 'string',
          description:
            'If provided, replaces ALL matches with this text. Preserves paragraph style. Use "" to delete all matches (equivalent to delete_content with query). Omit for search-only.',
        },
        replace_format: {
          type: 'object',
          description:
            'Apply inline formatting to the replaced text in the same call. Same fields as format_text: bold, italic, underline, underline_style, strikethrough, color (6-char hex, no #), size, font, highlight. Only applied when replace_with is provided. Example: {"bold":true,"color":"FF0000"}.',
        },
        match_case: {
          type: 'boolean',
          description: 'Case-sensitive search. Defaults to false.',
        },
        match_whole_word: {
          type: 'boolean',
          description:
            'Match whole words only (prevents "cat" from matching "category"). Defaults to false.',
        },
        match_wildcards: {
          type: 'boolean',
          description:
            'Interpret the query as a Word wildcard expression. Unlocks patterns like "to*n", "[aeiou]", "<Chapter*>", and numeric ranges. Use this to replace multiple chained searches with one pattern. Defaults to false.',
        },
        match_prefix: {
          type: 'boolean',
          description: 'Match only at the start of words. Defaults to false.',
        },
        match_suffix: {
          type: 'boolean',
          description: 'Match only at the end of words. Defaults to false.',
        },
        ignore_punct: {
          type: 'boolean',
          description: 'Ignore punctuation inside matches. Defaults to false.',
        },
        ignore_space: {
          type: 'boolean',
          description: 'Ignore whitespace inside matches. Defaults to false.',
        },
      },
      required: ['query'],
    },
    permission: true,
    isDestructive: (args) => typeof args.replace_with === 'string',
    validateInput: (args) => {
      const query = args.query as string;
      if (!query || query.trim().length === 0) {
        return { error: 'query is empty.', recovery: 'Provide the text to search for.' };
      }
      if (query.length > 255) {
        return {
          error: `query is ${query.length} characters; Word.search caps at 255.`,
          recovery:
            'Use a short unique phrase (3–15 words) from within the target text. If you need to act on a long passage, use insert_content with location="before_paragraph" / "after_paragraph" using paragraph_index from get_document instead.',
        };
      }
      if (/\\["'`]/.test(query)) {
        return {
          error: 'query appears to contain escape sequences (backslash + quote). Word.search treats these literally.',
          recovery:
            'Send the raw text — JSON handles quoting automatically. Example: query: "He said \\"hi\\"" (NOT with doubled backslashes).',
        };
      }
      if (args.replace_format !== undefined) {
        if (typeof args.replace_with !== 'string') {
          return {
            error: 'replace_format requires replace_with.',
            recovery: 'Provide replace_with (possibly "") alongside replace_format.',
          };
        }
        if (typeof args.replace_format !== 'object' || args.replace_format === null) {
          return {
            error: 'replace_format must be an object.',
            recovery: 'Example: {"bold":true,"color":"FF0000"}.',
          };
        }
        const f = args.replace_format as Record<string, unknown>;
        const provided = REPLACE_FORMAT_KEYS.filter((k) => k in f);
        if (provided.length === 0) {
          return {
            error: 'replace_format has no formatting properties.',
            recovery: `Include at least one of: ${REPLACE_FORMAT_KEYS.join(', ')}.`,
          };
        }
        const err = validateReplaceFormat(f);
        if (err) return err;
      }
      return null;
    },
    getActivityDescription: (args) => {
      const q =
        typeof args.query === 'string' && args.query.length > 40
          ? args.query.slice(0, 40) + '…'
          : args.query;
      if (typeof args.replace_with === 'string') {
        return args.replace_format ? `Replacing & styling "${q}"` : `Replacing "${q}"`;
      }
      return `Searching for "${q}"`;
    },
    execute: async (args) => {
      const query = args.query as string;
      const replaceFormat = args.replace_format as Record<string, unknown> | undefined;

      return await Word.run(async (context) => {
        const results = context.document.body.search(query, {
          matchCase: args.match_case === true,
          matchWholeWord: args.match_whole_word === true,
          matchWildcards: args.match_wildcards === true,
          matchPrefix: args.match_prefix === true,
          matchSuffix: args.match_suffix === true,
          ignorePunct: args.ignore_punct === true,
          ignoreSpace: args.ignore_space === true,
        });
        results.load('text');
        await context.sync();

        const matches = results.items.map((r) => r.text);

        if (typeof args.replace_with === 'string') {
          const replacedRanges: Word.Range[] = [];
          for (const range of results.items) {
            const newRange = range.insertText(args.replace_with as string, Word.InsertLocation.replace);
            replacedRanges.push(newRange);
          }
          await context.sync();

          if (replaceFormat && replacedRanges.length > 0) {
            for (const r of replacedRanges) applyReplaceFormat(r, replaceFormat);
            await context.sync();
          }

          const response: Record<string, unknown> = {
            query,
            replace_with: args.replace_with,
            replacements: matches.length,
            hint:
              matches.length === 0
                ? 'No matches found — nothing was replaced. Use get_document to verify the exact text.'
                : `Replaced ${matches.length} occurrence${matches.length > 1 ? 's' : ''}. Paragraph styles were preserved.`,
          };

          if (replaceFormat) {
            const applied: Record<string, unknown> = {};
            for (const k of REPLACE_FORMAT_KEYS) if (k in replaceFormat) applied[k] = replaceFormat[k];
            response.replace_format_applied = applied;
          }
          if (query.length <= 2 && matches.length > 0) {
            response.warning = `Short query "${query}" matched ${matches.length} times. All were replaced. Use match_whole_word=true for safety with short queries.`;
          }

          return JSON.stringify(response);
        }

        return JSON.stringify({
          query,
          count: matches.length,
          matches,
          hint:
            matches.length === 0
              ? 'No matches found. Check spelling or try a shorter/broader query. Use get_document to see the full document text.'
              : undefined,
        });
      });
    },
  }),
];
